import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetContributingScores } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { Skeleton } from '@design-system/Skeleton';
import { NavBar } from '@design-system/NavBar';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { InfoSheet } from '@screens/HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { useKitType } from '@hooks/useKittype';

export const HealthContributingScore = () => {
  const navigate = useNavigate();
  const { id: scoreId } = useParams();
  const kitType = useKitType();

  const {
    data: contributingScoreData,
    isLoading: isContributingScoreAPILoading,
    isError: isContributingScoreAPIError,
  } = scoreId
    ? useGetContributingScores('L2', Number(scoreId))
    : useGetContributingScores('L2');

  const navigateToConditionsPage = (id: number, type: string) => {
    return navigate(type === 'L3' ? `/health/l3/${id}` : `/health/l4/${id}`);
  };

  const resultedContributingScoreContent = (kitType: string) => {
    switch (kitType) {
      case 'GI':
        return INFO_SHEET_CONTENT.contributingScoreGI;
      case 'OHI':
        return INFO_SHEET_CONTENT.contributingScoreOHI;
      case 'FBI':
        return INFO_SHEET_CONTENT.contributingScore;
      default:
        return INFO_SHEET_CONTENT.contributingScore;
    }
  }

  if (isContributingScoreAPIError) {
    return <ErrorLoadingPage variant="dark" />;
  }

  return (
    <section className="h-full mx-auto gap-6 page-container dark bg-black text-primary !max-w-full">
      <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" />
      <div className="pt-12">
        <div className="typography-h1">Contributing Scores</div>
        <span className="pt-4 typography-btn-large text-secondary">
          These scores factor into your Health Zones, reflecting interconnected
          functions and pathways that impact your overall health.
          <InfoSheet
            title={resultedContributingScoreContent(kitType || '').title}
            description={resultedContributingScoreContent(kitType || '').description}
          />
        </span>
      </div>
      {isContributingScoreAPILoading ? (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              className="w-full h-20 rounded-lg"
            />
          ))}
        </div>
      ) : (
        <>
          {contributingScoreData && contributingScoreData.length > 0 && (
            <div className="pt-6">
              <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6">
                {contributingScoreData?.map((zone) => (
                  <div key={`${zone.id}`}>
                    <ScoreCard
                      title={zone.title}
                      tag={zone.rank}
                      color={zone.rank_color || ''}
                      score={zone.scores}
                      variant="dark"
                      onClick={() => {
                        navigateToConditionsPage(zone.id, zone.score_type);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
